/**
 * Collection filters: AJAX apply.
 * - Desktop left sidebar: no Apply/Clear footer; apply on each checkbox change (AJAX).
 * - Drawer (mobile / filter-bar-only): Apply + Clear All in footer; apply only on Apply click.
 */
import { sectionRenderer } from '@theme/section-renderer';
import { FilterUpdateEvent, ThemeEvents } from '@theme/events';

const SHOW_MORE_CHECKBOX_CLASS = 'main-collection__show-more-checkbox';
/** Align with main-collection sidebar: sticky left column is min-width 1024px; below that uses the mobile drawer. */
const DESKTOP_BREAKPOINT = 1024;

/**
 * Move overflow filter options from <template data-filter-overflow> into the live DOM.
 * Called once when the user first expands a filter group via "Show more".
 * @param {Element} list - .main-collection__filter-checkbox-list
 */
function hydrateFilterOverflow(list) {
  const tmpl = list.querySelector('template[data-filter-overflow]');
  if (!tmpl) return;
  const content = tmpl.content.cloneNode(true);
  const footer = list.querySelector('.main-collection__filter-list-footer');
  if (footer) {
    list.insertBefore(content, footer);
  } else {
    list.appendChild(content);
  }
  tmpl.remove();
}

/** True when filters are in the left sidebar (desktop, not filter-bar-only). */
function isDesktopSidebar() {
  const main = document.querySelector('.main-collection');
  return (
    window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT}px)`).matches &&
    main &&
    !main.classList.contains('main-collection--filter-bar-only')
  );
}

/**
 * Desktop sidebar: set expanded/collapsed from data attributes.
 * When data-desktop-expansion-custom is true, opens the first data-desktop-expanded-count groups (theme: 2 when merchants enable "all closed" desktop mode).
 * Otherwise, if mobile drawer defaults are collapsed, expands every group on desktop (legacy).
 * Runs on load and after section morph (FilterUpdate) because inline scripts in the section do not re-execute.
 * @param {HTMLFormElement | null} form
 */
function syncCollectionFacetExpansion(form) {
  if (!form) return;
  const mobileDefault = form.getAttribute('data-mobile-filters-default') || 'closed';
  const desktopExpansionCustom = (form.getAttribute('data-desktop-expansion-custom') || '').trim() === 'true';
  const rawCount = form.getAttribute('data-desktop-expanded-count');
  const parsed =
    rawCount != null && String(rawCount).trim() !== '' ? parseInt(String(rawCount).trim(), 10) : NaN;
  const desktopExpandedCount = Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
  const desktopSidebar = isDesktopSidebar();

  /**
   * @param {Element} group
   * @param {boolean} expand
   */
  function setFilterGroupExpanded(group, expand) {
    const content = group.querySelector('.main-collection__filter-group-content');
    const header = group.querySelector('.main-collection__filter-group-header');
    const arrow = group.querySelector('.main-collection__filter-arrow');
    group.classList.remove('main-collection__filter-group--user-collapsed');
    if (expand) {
      group.classList.add('main-collection__filter-group--expanded');
      if (header) header.setAttribute('aria-expanded', 'true');
      if (content) content.style.removeProperty('max-height');
      if (arrow) arrow.style.transform = 'rotate(180deg)';
    } else {
      group.classList.remove('main-collection__filter-group--expanded');
      if (header) header.setAttribute('aria-expanded', 'false');
      if (content) content.style.maxHeight = '0';
      if (arrow) arrow.style.transform = 'rotate(0deg)';
    }
  }

  if (desktopSidebar && desktopExpansionCustom) {
    const groups = form.querySelectorAll('.main-collection__filter-group');
    const n = desktopExpandedCount;
    groups.forEach((group, index) => {
      setFilterGroupExpanded(group, index < n);
    });
  } else if (mobileDefault === 'closed' && desktopSidebar) {
    form.querySelectorAll('.main-collection__filter-group').forEach((group) => {
      setFilterGroupExpanded(group, true);
    });
  }
}

/**
 * Build filter URL from form state (checkboxes, price range, sort_by, limit).
 * @param {HTMLFormElement} form
 * @param {URL} currentUrl
 * @returns {string} Full URL string
 */
function buildFilterUrl(form, currentUrl) {
  const searchParams = new URLSearchParams();

  const limitParam = currentUrl.searchParams.get('limit');
  if (limitParam) searchParams.append('limit', limitParam);

  const sortByParam = currentUrl.searchParams.get('sort_by');
  if (sortByParam) searchParams.append('sort_by', sortByParam);

  const checkboxes = form.querySelectorAll('input[type="checkbox"]:checked');
  checkboxes.forEach((checkbox) => {
    if (checkbox.classList.contains(SHOW_MORE_CHECKBOX_CLASS)) return;
    if (checkbox.name && checkbox.value) searchParams.append(checkbox.name, checkbox.value);
  });

  const priceRangeContainers = form.querySelectorAll('.main-collection__price-range');
  priceRangeContainers.forEach((container) => {
    const minInput = container.querySelector('.main-collection__price-range-input--min');
    const maxInput = container.querySelector('.main-collection__price-range-input--max');
    if (!minInput || !maxInput) return;
    const rangeMin = parseFloat(minInput.getAttribute('min')) || 0;
    const rangeMax = parseFloat(maxInput.getAttribute('max')) || 0;
    const minValue = minInput.value !== '' ? parseFloat(minInput.value) : rangeMin;
    const maxValue = maxInput.value !== '' ? parseFloat(maxInput.value) : rangeMax;
    if ((minValue !== rangeMin || maxValue !== rangeMax) && minInput.name && maxInput.name) {
      if (minValue !== rangeMin) searchParams.append(minInput.name, minInput.value);
      if (maxValue !== rangeMax) searchParams.append(maxInput.name, maxInput.value);
    }
  });

  const query = searchParams.toString();
  return `${currentUrl.pathname}${query ? `?${query}` : ''}`;
}

/**
 * Close the filter drawer if open.
 */
function closeDrawer() {
  const sidebar = document.querySelector('.main-collection__sidebar');
  const overlay = document.querySelector('[data-collection-filter-overlay]');
  if (sidebar) sidebar.classList.remove('main-collection__sidebar--open');
  if (overlay) overlay.classList.remove('backdrop-scrim--visible');
  document.body.style.overflow = '';
}

/**
 * True if the form has any filter selection (checked filter checkbox or non-default price range).
 * @param {HTMLFormElement} form
 * @returns {boolean}
 */
function hasFilterSelections(form) {
  const checkboxes = form.querySelectorAll(
    `input[type="checkbox"]:checked:not(.${SHOW_MORE_CHECKBOX_CLASS})`
  );
  if (checkboxes.length > 0) return true;

  const priceRangeContainers = form.querySelectorAll('.main-collection__price-range');
  for (const container of priceRangeContainers) {
    const minInput = container.querySelector('.main-collection__price-range-input--min');
    const maxInput = container.querySelector('.main-collection__price-range-input--max');
    if (!minInput || !maxInput) continue;
    const rangeMin = parseFloat(minInput.getAttribute('min')) || 0;
    const rangeMax = parseFloat(maxInput.getAttribute('max')) || 0;
    const minValue = minInput.value !== '' ? parseFloat(minInput.value) : rangeMin;
    const maxValue = maxInput.value !== '' ? parseFloat(maxInput.value) : rangeMax;
    if (minValue !== rangeMin || maxValue !== rangeMax) return true;
  }
  return false;
}

/**
 * Toggle footer between "Close" only and "Clear Filter" + "Apply Filter" based on form state.
 * @param {HTMLFormElement} form
 */
function updateFooterState(form) {
  const footer = document.querySelector('.main-collection__sidebar-footer');
  if (!footer) return;
  const closeWrap = footer.querySelector('.main-collection__sidebar-footer-close-wrap');
  const actions = footer.querySelector('.main-collection__sidebar-footer-actions');
  if (!closeWrap || !actions) return;

  const hasSelections = hasFilterSelections(form);
  footer.dataset.footerState = hasSelections ? 'has-selections' : '';
  closeWrap.hidden = hasSelections;
  actions.hidden = !hasSelections;
}

/**
 * Apply filters via AJAX: fetch section HTML, morph, update history, close drawer, dispatch FilterUpdate.
 * @param {string} sectionId - Section instance id (e.g. template--123__main-collection)
 * @param {string} urlString - Full URL (pathname + search) to apply
 */
async function applyFilters(sectionId, urlString) {
  const url = new URL(urlString, window.location.origin);
  await sectionRenderer.renderSection(sectionId, { cache: false, url });
  history.pushState({}, '', urlString);
  closeDrawer();
  document.dispatchEvent(new FilterUpdateEvent(url.searchParams));
}

function init() {
  // Use delegation so handlers work after section morph (AJAX apply)
  document.addEventListener('submit', (e) => {
    const form = e.target?.closest?.('.main-collection form[id^="FacetFiltersForm-"]');
    if (!form) return;
    e.preventDefault();
    const sectionId = form.id.replace('FacetFiltersForm-', '');
    const currentUrl = new URL(window.location.href);
    const urlString = buildFilterUrl(form, currentUrl);
    applyFilters(sectionId, urlString);
  });

  document.addEventListener('click', (e) => {
    if (e.target?.closest?.('.main-collection__sidebar-footer-close')) {
      e.preventDefault();
      closeDrawer();
      return;
    }
    const clearBtn = e.target?.closest?.('.main-collection__sidebar-footer-clear');
    if (clearBtn) {
      e.preventDefault();
      const footer = document.querySelector('.main-collection__sidebar-footer');
      const form = document.querySelector('.main-collection form[id^="FacetFiltersForm-"]');
      const collectionBaseUrl = footer?.dataset.collectionUrl || (form && new URL(form.action || window.location.href).pathname) || window.location.pathname;
      const sectionId = form?.id?.replace('FacetFiltersForm-', '');
      if (!sectionId) return;
      const urlString = collectionBaseUrl.startsWith('http') ? new URL(collectionBaseUrl).pathname : collectionBaseUrl;
      applyFilters(sectionId, urlString);
      return;
    }
    const resetBtn = e.target?.closest?.('.main-collection__filter-reset');
    if (resetBtn) {
      e.preventDefault();
      const filterListId = resetBtn.getAttribute('data-filter-list');
      const form = document.querySelector('.main-collection form[id^="FacetFiltersForm-"]');
      if (!filterListId || !form) return;
      const list = form.querySelector(`.main-collection__filter-checkbox-list[data-filter-list="${filterListId}"]`);
      if (!list) return;
      list.querySelectorAll('.main-collection__filter-checkbox').forEach((cb) => {
        cb.checked = false;
      });
      const showMoreCheckbox = list.querySelector(`.${SHOW_MORE_CHECKBOX_CLASS}`);
      if (showMoreCheckbox) showMoreCheckbox.checked = false;
      updateFooterState(form);
      const sectionId = form.id.replace('FacetFiltersForm-', '');
      const currentUrl = new URL(window.location.href);
      const urlString = buildFilterUrl(form, currentUrl);
      applyFilters(sectionId, urlString);
    }
  });

  document.addEventListener('change', (e) => {
    const form = e.target?.closest?.('.main-collection form[id^="FacetFiltersForm-"]');
    if (!form) return;
    if (e.target?.classList?.contains(SHOW_MORE_CHECKBOX_CLASS)) {
      const group = e.target.closest('.main-collection__filter-group');
      const content = group?.querySelector('.main-collection__filter-group-content');
      if (content) content.style.removeProperty('max-height');
      if (e.target.checked) {
        const list = e.target.closest('.main-collection__filter-checkbox-list');
        if (list) hydrateFilterOverflow(list);
      }
    }
    const isFilterInput =
      e.target?.matches?.(`input[type="checkbox"]:not(.${SHOW_MORE_CHECKBOX_CLASS})`) ||
      e.target?.matches?.('.main-collection__price-range-input--min, .main-collection__price-range-input--max');
    if (isFilterInput) {
      updateFooterState(form);
    }
    if (isDesktopSidebar() && isFilterInput) {
      if (window.__collectionFiltersApplyTimeout) clearTimeout(window.__collectionFiltersApplyTimeout);
      window.__collectionFiltersApplyTimeout = setTimeout(() => {
        window.__collectionFiltersApplyTimeout = null;
        const sectionId = form.id.replace('FacetFiltersForm-', '');
        const currentUrl = new URL(window.location.href);
        const urlString = buildFilterUrl(form, currentUrl);
        applyFilters(sectionId, urlString);
      }, 150);
    }
  });

  document.addEventListener('input', (e) => {
    if (!e.target?.matches?.('.main-collection__price-range-input--min, .main-collection__price-range-input--max')) return;
    const form = e.target?.closest?.('.main-collection form[id^="FacetFiltersForm-"]');
    if (form) updateFooterState(form);
  });

  // Initial footer state (facet expansion: filters.liquid DOMContentLoaded + FilterUpdate — inline scripts do not run after morph)
  const form = document.querySelector('.main-collection form[id^="FacetFiltersForm-"]');
  if (form) updateFooterState(form);

  document.addEventListener(ThemeEvents.FilterUpdate, () => {
    requestAnimationFrame(() => {
      const f = document.querySelector('.main-collection form[id^="FacetFiltersForm-"]');
      if (f) syncCollectionFacetExpansion(f);
    });
  });

  // Refresh footer state when drawer opens (so correct state after AJAX morph or first open)
  document.addEventListener('click', (e) => {
    if (!e.target?.closest?.('.main-collection__filter-button')) return;
    setTimeout(() => {
      const f = document.querySelector('.main-collection form[id^="FacetFiltersForm-"]');
      if (f) updateFooterState(f);
    }, 0);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

/** Exposed for filters.liquid inline script (DOMContentLoaded); module runs before that listener. */
window.__syncCollectionFacetExpansion = syncCollectionFacetExpansion;
